import type {
	ExtensionManifest,
	ExtensionComponentProps,
} from '@atlaskit/editor-common/extensions';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';

import { fg } from '@confluence/feature-gating';
import type { LegacyContentMacroPlugin } from '@confluence/experiment-legacy-content-macro-plugin';

import { defaultDocument } from './default-document.ts';
import type { LegacyContentMacroParams } from './legacy-content-macro-params.ts';

// Note: This is a function rather than static manifest definition as it utilises feature flags that
// are only available at runtime.
const createManifest = (
	outerEditorView: EditorView | undefined,
	outerEditorApi: PublicPluginAPI<[ExtensionPlugin, LegacyContentMacroPlugin]> | undefined,
	propOverrides?: { contentMode?: 'standard' | 'compact' | undefined },
): ExtensionManifest<LegacyContentMacroParams> => ({
	title: 'Legacy Content Macro',
	type: 'com.atlassian.confluence.migration',
	key: 'legacy-content',
	description: 'Allows editing TinyMCE content',
	icons: {
		'16': () =>
			import(/* webpackChunkName: "loadable-editor-activity" */ '@atlaskit/icon/core/dashboard'),
		'24': () =>
			import(/* webpackChunkName: "loadable-editor-activity" */ '@atlaskit/icon/core/dashboard'),
		'48': () =>
			import(/* webpackChunkName: "loadable-editor-activity" */ '@atlaskit/icon/core/dashboard'),
	},
	modules: {
		quickInsert: fg('platform_editor_legacy_content_macro_insert')
			? [
					{
						key: 'item',
						action: {
							type: 'node',
							key: 'default',
							parameters: {
								nestedContent: defaultDocument,
								adf: JSON.stringify(defaultDocument),
							},
						},
					},
				]
			: [],
		nodes: {
			default: {
				type: 'extension',
				render: () => {
					return import(
						/* webpackChunkName: "loadable-legacy-content-macro" */ './extension-handler.tsx'
					).then((module) => {
						const defaultHandler = module.default;

						/*
							- In extension-handler.ts only the node is available as a param, so we overwrite this to also have the outer editor view & outer editor API.
							- The handler function 'render' for extension nodes is constantly triggered, which will cause the LCM to re-render over and over.
							  We're aware of this issue, so for now we're disabling render tracking to avoid analytic events spam. We pass a flag in through the feature
							  flags prop in the NBM preset - lcm-prevent-render-tracking. Which is then checked in editor-internal and ReactEditorViewNext.
						*/
						const wrappedHandler = (node: ExtensionComponentProps<LegacyContentMacroParams>) => {
							return defaultHandler({
								...node,
								outerEditorView,
								outerEditorApi,
								propOverrides,
							});
						};

						return wrappedHandler;
					});
				},
			},
		},
	},
});

// eslint-disable-next-line import/no-default-export
export default createManifest;
